import { Box, Button, Flex, Modal as ReeferModal } from '@iheartjane/reefer';
import isEmpty from 'lodash-es/isEmpty';
import noop from 'lodash-es/noop';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useCartActions } from '@jane/monolith/components/cart/cartFacade/useCartActions';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import {
  clearCart,
  clearRemovedCrmRedemptions,
  dismissCheckoutError,
  getRemoteCart,
  removeBrandSpecialHasChangedFlag,
  removeSpecialHasChangedFlag,
  selectCart,
  unavailableCheckoutFixed,
} from '@jane/monolith/customer/redux/cart';
import { closeModal } from '@jane/monolith/customer/redux/modal';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { getUnavailableProductIds } from '@jane/monolith/lib/cart';
import { useIframeScroll, useIsInIframe } from '@jane/shared-ecomm/hooks';
import { useEcommApp } from '@jane/shared/providers';
import { postMessageToIframeParent } from '@jane/shared/util';

import { CartAlertsForm } from './form';

export const CartAlerts = ({
  goBack,
  isCheckoutAlert,
  onClose,
}: {
  goBack?: () => void;
  isCheckoutAlert?: boolean;
  onClose?: () => void;
}) => {
  const navigate = useNavigate();
  const { cart, isFixingUnavailableCheckout } = useCustomerSelector(selectCart);
  const { store } = useCustomerSelector(selectStore);

  const { isEmbed } = useEcommApp();
  const isInIframe = useIsInIframe();
  const dispatch = useCustomerDispatch();
  const { deleteUnavailableProducts } = useCartActions();

  const {
    brand_special_has_changed,
    special_has_changed,
    unavailable_products,
    products,
    removed_crm_redemptions,
    quantity_adjusted_products,
  } = cart;

  const onClick = useCallback(
    (doClose: boolean) => {
      if (unavailable_products.length && !products.length) {
        dispatch(clearCart());
      }
      if (unavailable_products.length) {
        const ids = getUnavailableProductIds(unavailable_products);
        deleteUnavailableProducts(ids);
      }

      if (brand_special_has_changed) {
        dispatch(removeBrandSpecialHasChangedFlag());
      }

      if (special_has_changed) {
        dispatch(removeSpecialHasChangedFlag());
      }

      if (removed_crm_redemptions) {
        dispatch(clearRemovedCrmRedemptions());
      }

      if (isCheckoutAlert) {
        dispatch(dismissCheckoutError());
      }

      if (isFixingUnavailableCheckout) {
        dispatch(unavailableCheckoutFixed());

        // cart has been reseted by checkout
        if (cart.uuid === '' && unavailable_products.length === 0) {
          if (goBack) {
            goBack();
          }
          // cart was emptied by the cartdrawer
        } else if (cart.uuid && isEmpty(cart.products)) {
          dispatch(clearCart());
        }
      }

      if (quantity_adjusted_products && quantity_adjusted_products.length > 0) {
        dispatch(getRemoteCart(cart.uuid));
      }

      // if we have an onClose behavior ignore the redux dispatch
      if (doClose && !onClose) {
        dispatch(closeModal());
      } else if (onClose) {
        onClose();
      }
    },
    [navigate]
  );

  useIframeScroll(isInIframe);

  if (isEmbed && !isCheckoutAlert) {
    return (
      <Box mx="auto" maxWidth={360} pt={24}>
        <CartAlertsForm
          isFixingUnavailableCheckout={isFixingUnavailableCheckout}
          cart={cart}
          store={store}
        />
        <Button
          mt={24}
          full
          variant="primary"
          label={brand_special_has_changed ? 'OK' : 'Got it!'}
          onClick={() => {
            if (unavailable_products) {
              postMessageToIframeParent({
                messageType: 'unavailableProductsEvent',
                payload: {
                  unavailable_products,
                  products,
                },
              });
            }
            onClick(false);
          }}
        />
      </Box>
    );
  }

  if (isCheckoutAlert && isEmbed) {
    return (
      <ReeferModal
        appId="root"
        open
        onRequestClose={onClose ?? noop}
        variant="dialogue"
        overflow="hidden"
        topOverride={isInIframe ? '160px' : undefined}
      >
        <Flex maxWidth={325} flexDirection="column" p={24}>
          <CartAlertsForm
            isFixingUnavailableCheckout={isFixingUnavailableCheckout}
            cart={cart}
            store={store}
          />

          <Button
            mt={24}
            full
            variant="primary"
            label="Got it!"
            onClick={() => onClick(false)}
          />
        </Flex>
      </ReeferModal>
    );
  }

  return (
    <ReeferModal open variant="dialogue" onRequestClose={() => onClick(true)}>
      <ReeferModal.Content>
        <Flex width="279px" flexDirection="column" alignItems="center">
          <CartAlertsForm
            cart={cart}
            isFixingUnavailableCheckout={isFixingUnavailableCheckout}
            store={store}
          />

          <Button
            mt={24}
            full
            variant="primary"
            label={brand_special_has_changed ? 'OK' : 'Got it!'}
            onClick={() => onClick(true)}
          />
        </Flex>
      </ReeferModal.Content>
    </ReeferModal>
  );
};
